import { isSameDayInTimezone } from "@compass/shared/schema/dateUtils";
import { Label } from "@radix-ui/react-dropdown-menu";
import dayjs from "dayjs";
import { useState } from "react";
import { ReactComponent as CalendarIcon } from "../../assets/img/events/calendar.svg";
import { ReactComponent as DateRangeIcon } from "../../assets/img/events/date.svg";
import { ReactComponent as LocationIcon } from "../../assets/img/events/location.svg";
import { ReactComponent as PriceIcon } from "../../assets/img/events/price.svg";
import { ReactComponent as TimeIcon } from "../../assets/img/events/time.svg";
import { Button } from "components/elements";
import { FavoriteButton } from "components/elements/FavoriteButton";
import { useUserTimezone } from "hooks/useUserTimezone";
import { CalendarFriendlyEvent, dateRangeToDisplay, formatDate, timeToDisplay } from "./types";

type AllEventsProps = {
    futureEventsFormatted: CalendarFriendlyEvent[];
    onSelectEvent: (event: CalendarFriendlyEvent) => void;
    onOpenModal: (event: CalendarFriendlyEvent) => void;
    onOpenEventDetails: (event: CalendarFriendlyEvent) => void;
};

export const AllEvents = ({
    futureEventsFormatted,
    onOpenEventDetails,
    onOpenModal,
}: AllEventsProps) => {
    const timeZone = useUserTimezone();
    const filterKeys = ["month", "week", "today", "all"] as const;
    type FilterKey = (typeof filterKeys)[number];
    const [activeFilter, setActiveFilter] = useState<FilterKey>("month");

    const now = new Date();

    const filteredEvents = futureEventsFormatted.filter((event) => {
        const eventDate = new Date(event.start);

        switch (activeFilter) {
            case "month":
                return (
                    eventDate.getMonth() === now.getMonth() &&
                    eventDate.getFullYear() === now.getFullYear()
                );
            case "week": {
                const startOfWeek = new Date(now);
                startOfWeek.setDate(now.getDate() - now.getDay());
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                return eventDate >= startOfWeek && eventDate <= endOfWeek;
            }
            case "today":
                return isSameDayInTimezone(eventDate, now, timeZone);
            case "all":
            default:
                return true;
        }
    });

    return (
        <div className="h-fit rounded-2xl border border-[#c8c8c8] bg-white p-6">
            <p className="mb-2 font-sans text-[20px] font-semibold leading-[100%] tracking-[0] text-[#424242]">
                All Events
            </p>

            <div className="mt-2 mb-4 flex w-full justify-between border-b border-t border-[#bdbdbd] py-3">
                {filterKeys.map((key) => {
                    const label = {
                        month: "This Month",
                        week: "This Week",
                        today: "Today",
                        all: "My Events",
                    }[key];

                    const isActive = activeFilter === key;

                    return (
                        <div
                            key={key}
                            role="button"
                            onClick={() => setActiveFilter(key)}
                            className="relative cursor-pointer px-4 py-1 font-sans text-[16px] font-semibold leading-[100%] tracking-[0] text-[#424242]"
                        >
                            {label}
                            {isActive && (
                                <span className="absolute -bottom-3 left-0 h-0.5 w-full bg-[#2da7e0]"></span>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="h-[450px] space-y-6 overflow-auto">
                {filteredEvents.length === 0 ? (
                    <div className="shadow-soft rounded-xl border border-gray-200 bg-white p-8 text-center">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                            <CalendarIcon className="" />
                        </div>
                        <h3 className="mb-2 text-lg font-semibold text-gray-900">
                            No events found
                        </h3>
                        <p className="text-gray-600">No events match your current filter</p>
                    </div>
                ) : (
                    filteredEvents.map((item) => {
                        const date = new Date(item.start);
                        const month = new Intl.DateTimeFormat("en-US", { month: "short", timeZone })
                            .format(date)
                            .toUpperCase();
                        const day = new Intl.DateTimeFormat("en-US", {
                            day: "2-digit",
                            timeZone,
                        }).format(date);
                        const year = new Intl.DateTimeFormat("en-US", {
                            year: "numeric",
                            timeZone,
                        }).format(date);

                        return (
                            <div
                                key={item.id}
                                className="flex h-20 w-full cursor-pointer overflow-hidden rounded-lg border border-[#fafafa] bg-white shadow-sm"
                            >
                                <div
                                    className={`
                                    flex h-full w-16 flex-col  items-center justify-center p-2 text-white
                                    ${item.type === "COMPASS" ? "bg-[#8bbfd9]" : "bg-[#2DA7E0]"}
                                    `}
                                >
                                    <span className="font-sans text-[14px] font-semibold uppercase leading-[100%] tracking-[0] text-white">
                                        {month}
                                    </span>
                                    <span className="font-sans text-[25px] font-semibold leading-[100%] tracking-[0] text-white">
                                        {day}
                                    </span>
                                    {/*<span className="text-lg">{year}</span>*/}
                                </div>
                                <div className="flex flex-1 items-center justify-between px-4 py-2">
                                    <div>
                                        <div
                                            className="mb-2 font-sans text-[16px] font-semibold leading-[100%] tracking-[0] text-[#424242]"
                                            onClick={() => onOpenEventDetails(item)}
                                        >
                                            {item.title}
                                        </div>
                                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
                                            {item.start && (
                                                <div className="flex items-center gap-1">
                                                    <DateRangeIcon className="h-5 w-5" />
                                                    <div>
                                                        {dateRangeToDisplay(
                                                            item.start,
                                                            item?.end,
                                                            timeZone,
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/*item.end && (
                                                <div className="flex items-center gap-1">
                                                    <TimeIcon className="h-5 w-5" />
                                                    <div>
                                                        {item.start
                                                            ? timeToDisplay(new Date(item.start))
                                                            : ""}
                                                        {item.end && item.start !== item.end && (
                                                            <>
                                                                {" "}
                                                                —{" "}
                                                                {timeToDisplay(new Date(item.end))}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            )*/}

                                            {item.location && (
                                                <div className="flex items-center gap-0">
                                                    <LocationIcon className="h-5 w-5" />
                                                    <div>
                                                        {item.location && (
                                                            <span>{item.location}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {item.priceRange && (
                                                <div className="flex items-center gap-0">
                                                    <PriceIcon className="h-5 w-5" />
                                                    <div>
                                                        {item.priceRange && (
                                                            <span>{item.priceRange}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <FavoriteButton
                                        target={{ type: "event", id: item.id }}
                                        size={16}
                                    />
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};
